import { useState, useEffect } from "react";
import "@/App.css";
import "@/sections.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  Shield,
  Swords,
  Truck,
  Handshake,
  TrendingUp,
  Users,
  Route as RouteIcon,
  ShieldCheck,
  Lock,
  ExternalLink,
  Menu,
  X,
  Send,
  Check,
  Plus,
  Mail,
  Clock,
  Globe,
  Loader2,
} from "lucide-react";
import { Privacy, Terms, Disclaimer } from "@/pages/Legal";
import Constellation from "@/components/Constellation";
import SiteFooter from "@/components/SiteFooter";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_dispatch-command-17/artifacts/4l0ay7vy_Screenshot_20260712_215824_Gallery.jpg";
const DSS_BANNER_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_dispatch-command-17/artifacts/ygupeq9a_file_000000006e2c71fda106d2bed875e2e6.png";
const DSS_LINK = "https://blackknightdss.life";
const OPS_EMAIL = "Bktoperations@blackknighttrans.com";

const NAV = [
  { href: "#about", label: "Overview" },
  { href: "#solutions", label: "Solutions" },
  { href: "#how", label: "How It Works" },
  { href: "#dss-addon", label: "DSS Engine" },
  { href: "#faq", label: "FAQ" },
  { href: "#recruiting", label: "Join Our Network" },
  { href: "#contact", label: "Contact" },
];

const BADGES = [
  { icon: Shield, title: "Protecting", sub: "Your Freight" },
  { icon: Swords, title: "Powering", sub: "Your Operations" },
  { icon: Truck, title: "Delivering", sub: "Peace of Mind" },
  { icon: Handshake, title: "Built on Trust", sub: "Driven by Excellence" },
];

const STATS = [
  { num: "Est. 2025", label: "Trusted Operations" },
  { num: "24/7", label: "Dispatch Coverage" },
  { num: "100%", label: "Broker Vetting" },
  { num: "$0", label: "Setup Fees" },
];

const CAPABILITIES = [
  {
    icon: RouteIcon,
    title: "Premium Freight Management",
    body: "A dedicated dispatching service handling the logistics forefront. We secure top-tier market rates, optimize lane matrices, and process continuous back-office paperwork so carriers stay focused on the road.",
  },
  {
    icon: TrendingUp,
    title: "Carrier Revenue Scale",
    body: "Aggressive rate negotiations, comprehensive credit checks on brokers, and deliberate route structuring designed explicitly to elevate bottom-line margins for owner-operators and independent fleets.",
  },
  {
    icon: Users,
    title: "Elite Dispatch Recruiting",
    body: "Sourcing, vetting, and onboarding high-caliber dispatch talent and premium logistics coordinators to expand operational networks and manage specialized freight streams.",
  },
];

const STEPS = [
  { n: "01", title: "Submit Your Credentials", body: "Send us your authority, equipment type, and lane preferences through the intake pipeline below. Onboarding is fast and paperless." },
  { n: "02", title: "We Dispatch & Negotiate", body: "Our team hunts high-paying loads, vets every broker for creditworthiness, and handles rate confirmations and back-office paperwork." },
  { n: "03", title: "You Drive & Get Paid", body: "Stay focused on the road while we maximize your revenue per mile, minimize deadhead, and keep your wheels turning profitably." },
];

const POLICIES = [
  {
    icon: ShieldCheck,
    title: "Broker & Shipper Vetting Standard",
    body: "Black Knight Transportation operates under strict financial compliance protocols. Every broker and shipper profile must pass rigorous credit-worthiness and bonding assessments prior to dispatch assignment, protecting our carrier partners from non-payment liabilities.",
  },
  {
    icon: Truck,
    title: "Carrier Onboarding & Safety Verification",
    body: "As a dispatch recruiting and management provider, we mandate absolute alignment with FMCSA safety ratings. We strictly coordinate with carriers maintaining active authority, valid insurance requirements, and exemplary DOT compliance backgrounds.",
  },
  {
    icon: Lock,
    title: "Data Protection & Dispatch Protocols",
    body: "All rate confirmations, proprietary lane analytics, and client communication data routed through our ecosystem or secondary engines like Black Knight DSS are heavily secured to maintain trade-secret integrity and operational security.",
  },
];

const PRICING = [
  "Transparent flat percentage of gross revenue — disclosed upfront",
  "No forced dispatch, ever — you approve every load",
  "No long-term contracts — cancel anytime",
  "$0 setup and onboarding fees",
  "Full back-office paperwork & broker credit checks included",
];

const TESTIMONIALS = [
  { quote: "Black Knight took the load-hunting stress off my plate and my revenue per mile jumped almost immediately. They treat my truck like it's their own.", name: "Owner-Operator", role: "Dry Van · Midwest" },
  { quote: "The broker vetting alone is worth it — I stopped chasing payments and started focusing on driving. True sword and shield.", name: "Carrier", role: "Reefer · Southeast" },
  { quote: "Joined their dispatch recruiting network and the onboarding was seamless. Professional, sharp, and always available.", name: "Dispatcher", role: "Logistics Coordinator" },
];

const FAQS = [
  { q: "Are you a broker or a motor carrier?", a: "Neither. Black Knight Transportation LLC is a freight dispatch service. We work on behalf of carriers who hold their own operating authority — we never take possession of freight or act as a broker." },
  { q: "How do you charge for dispatch?", a: "We charge a transparent flat percentage of your gross load revenue, disclosed before you onboard. No setup fees, no hidden costs, and no forced dispatch." },
  { q: "What equipment types do you dispatch?", a: "We dispatch dry van, reefer, flatbed, and specialized freight. Tell us your equipment in the intake form and we'll match you to the right lanes." },
  { q: "Do I need my own authority and insurance?", a: "Yes. Carriers must maintain active operating authority, valid insurance meeting federal minimums, and a satisfactory FMCSA safety rating." },
  { q: "Can I join your dispatch recruiting network?", a: "Absolutely. Select 'Logistics Coordinator / Dispatcher' in the intake form and share your background — we're always recruiting high-caliber talent." },
];

const EQUIPMENT = ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Hotshot", "Specialized", "48-State Coverage"];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Header({ menuOpen, setMenuOpen }) {
  return (
    <header className="bk-header" data-testid="site-header">
      <div className="bk-nav">
        <div className="bk-brand" data-testid="nav-brand">
          <img src={LOGO_URL} alt="Black Knight" className="bk-brand-mark" />
          Black Knight <span>Operations</span>
        </div>
        <nav className="bk-nav-links" data-testid="nav-links">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className={n.href === "#recruiting" ? "cta-nav-btn" : ""}
              data-testid={`nav-link-${n.href.slice(1)}`}
            >
              {n.label}
            </a>
          ))}
        </nav>
        <button
          className="bk-menu-btn"
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>
      <div className={`bk-mobile-menu ${menuOpen ? "open" : ""}`} data-testid="mobile-menu">
        {NAV.map((n) => (
          <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-link-${n.href.slice(1)}`}>
            {n.label}
          </a>
        ))}
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="bk-hero" id="about" data-testid="hero-section">
      <motion.div
        className="bk-logo-wrap"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="bk-aurora" aria-hidden="true" />
        <img src={LOGO_URL} alt="Black Knight Transportation LLC Logo" className="bk-logo" data-testid="hero-logo" />
      </motion.div>

      <motion.h1
        className="bk-h1"
        data-testid="hero-h1"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.28, duration: 0.6 }}
      >
        Elite <span>Freight Dispatch</span> &amp; <span>Dispatch Recruiting</span>
      </motion.h1>

      <motion.div className="bk-tagline" data-testid="hero-tagline" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }}>
        The Sword and Shield of Freight
      </motion.div>

      <motion.p className="bk-hero-sub" data-testid="hero-subtitle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55, duration: 0.6 }}>
        Black Knight Transportation LLC is a premium freight dispatch and dispatch-recruiting company. We help
        independent owner-operators and motor carriers maximize revenue per mile through expert load-board
        management, aggressive rate negotiation, broker credit vetting, and full back-office support — so you
        stay focused on the road while we command the movement.
      </motion.p>

      <motion.div className="bk-cta-row" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}>
        <a href="#recruiting" className="bk-btn bk-btn-cyan-solid" data-testid="hero-cta-primary">Join Our Network</a>
        <a href="#solutions" className="bk-btn bk-btn-cyan" data-testid="hero-cta-secondary">Explore Solutions</a>
      </motion.div>

      <div className="bk-badges" data-testid="trust-badges">
        {BADGES.map((b, i) => (
          <motion.div className="bk-badge" key={b.title} data-testid={`trust-badge-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <b.icon className="bk-badge-icon" size={30} strokeWidth={1.6} />
            <div>
              <div className="bk-badge-title">{b.title}</div>
              <div className="bk-badge-sub">{b.sub}</div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bk-stats" data-testid="stats-strip">
        {STATS.map((s, i) => (
          <motion.div className="bk-stat" key={s.label} data-testid={`stat-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <div className="bk-stat-num">{s.num}</div>
            <div className="bk-stat-label">{s.label}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Solutions() {
  return (
    <section className="bk-section" id="solutions" data-testid="solutions-section">
      <div className="bk-eyebrow">What We Do</div>
      <h2 className="bk-section-title">Elite <span>Dispatch Solutions</span></h2>
      <div className="bk-cap-grid">
        {CAPABILITIES.map((c, i) => (
          <motion.div className="bk-cap-card" key={c.title} data-testid={`capability-card-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <div className="bk-cap-icon"><c.icon size={26} strokeWidth={1.7} /></div>
            <h3>{c.title}</h3>
            <p>{c.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="bk-section" id="how" data-testid="how-section">
      <div className="bk-eyebrow">The Process</div>
      <h2 className="bk-section-title">How It <span>Works</span></h2>
      <div className="bk-steps">
        {STEPS.map((s, i) => (
          <motion.div className="bk-step" key={s.n} data-testid={`step-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <div className="bk-step-num">{s.n}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section className="bk-section" id="pricing" data-testid="pricing-section">
      <div className="bk-eyebrow">Pricing</div>
      <h2 className="bk-section-title">Transparent <span>Partnership</span></h2>
      <div className="bk-pricing">
        <div className="bk-pricing-head">
          <div className="bk-price-tag">Flat % of Gross</div>
          <p>No games, no hidden fees. You keep control of every load, and we only win when you win.</p>
        </div>
        <ul className="bk-pricing-list">
          {PRICING.map((p) => (
            <li key={p}><Check size={18} strokeWidth={2.4} />{p}</li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function DssModule() {
  return (
    <motion.section className="bk-section" id="dss-addon" data-testid="dss-section" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
      <div className="bk-dss">
        <img src={DSS_BANNER_URL} alt="Black Knight DSS" className="bk-dss-banner" data-testid="dss-banner" />
        <div className="bk-dss-body">
          <div className="bk-dss-header">
            <div className="bk-dss-title">BLACK KNIGHT DSS<span className="bk-dss-badge">AUXILIARY DIGITAL TOOL</span></div>
            <button className="bk-btn bk-btn-emerald" data-testid="dss-launch-button" onClick={() => window.open(DSS_LINK, "_blank")}>
              Launch Subsystem <ExternalLink size={16} />
            </button>
          </div>
          <p className="bk-dss-text">
            Accelerate your logistics workflow with our optional standalone tech stack. Black Knight DSS offers
            auxiliary data metrics, tracking modules, and scheduling tools built to seamlessly link out to{" "}
            <a href={DSS_LINK} target="_blank" rel="noopener noreferrer" data-testid="dss-link">blackknightdss.life</a>.
          </p>
        </div>
      </div>
    </motion.section>
  );
}

function Testimonials() {
  return (
    <section className="bk-section" id="testimonials" data-testid="testimonials-section">
      <div className="bk-eyebrow">Testimonials</div>
      <h2 className="bk-section-title">Carrier <span>Voices</span></h2>
      <div className="bk-testi-grid">
        {TESTIMONIALS.map((t, i) => (
          <motion.div className="bk-testi" key={i} data-testid={`testimonial-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <div className="bk-testi-stars">★★★★★</div>
            <p className="bk-testi-quote">“{t.quote}”</p>
            <div className="bk-testi-head">
              <div className="bk-testi-avatar">{t.name.charAt(0)}</div>
              <div>
                <div className="bk-testi-name">{t.name}</div>
                <div className="bk-testi-role">{t.role}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Policies() {
  return (
    <section className="bk-section" id="compliance" data-testid="policies-section">
      <div className="bk-eyebrow">Compliance</div>
      <h2 className="bk-section-title">Operational <span>Policies &amp; Procedures</span></h2>
      <div className="bk-policy">
        {POLICIES.map((p, i) => (
          <motion.div className="bk-policy-block" key={p.title} data-testid={`policy-block-${i}`} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}>
            <h4><p.icon size={20} strokeWidth={1.8} />{p.title}</h4>
            <p>{p.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="bk-disclaimer" data-testid="fmcsa-disclaimer">
        <strong>Compliance Notice:</strong> Black Knight Transportation LLC is a professional freight dispatch
        &amp; recruiting service. We are not a motor carrier, freight broker, or forwarder, and do not take
        possession of freight. All loads move under the carrier's own authority and insurance.{" "}
        <Link to="/disclaimer" style={{ color: "#00FF66" }} data-testid="disclaimer-link">Read full disclaimer</Link>.
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="bk-section" id="faq" data-testid="faq-section">
      <div className="bk-eyebrow">Answers</div>
      <h2 className="bk-section-title">Frequently <span>Asked Questions</span></h2>
      {FAQS.map((f, i) => (
        <div className="bk-faq-item" key={i} data-testid={`faq-item-${i}`}>
          <button className={`bk-faq-q ${open === i ? "open" : ""}`} onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-question-${i}`}>
            {f.q} <Plus size={20} />
          </button>
          {open === i && <div className="bk-faq-a" data-testid={`faq-answer-${i}`}>{f.a}</div>}
        </div>
      ))}
    </section>
  );
}

function Recruiting() {
  const [form, setForm] = useState({ name: "", email: "", profile_type: "", mc_dot: "", background: "", company_website: "" });
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState({ type: null, msg: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, msg: "" });
    if (!form.profile_type) {
      setStatus({ type: "error", msg: "Please select an application type." });
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (file) data.append("document", file);
      await axios.post(`${API}/intake`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus({ type: "success", msg: "Pipeline request transmitted. Our operations team will reach out shortly." });
      setForm({ name: "", email: "", profile_type: "", mc_dot: "", background: "", company_website: "" });
      setFile(null);
      e.target.reset();
    } catch (err) {
      const msg =
        err?.response?.status === 429
          ? "You've submitted several times recently. Please wait a bit and try again."
          : "Transmission failed. Please try again or email us directly.";
      setStatus({ type: "error", msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bk-section" id="recruiting" data-testid="recruiting-section">
      <div className="bk-hiring">
        <div className="bk-eyebrow">Get Started</div>
        <h2 className="bk-section-title">Carrier Intake &amp; <span>Dispatch Recruiting</span></h2>
        <p className="bk-hiring-intro">
          Independent Owner-Operators looking for professional dispatch representation, or professional
          dispatchers looking to join our recruitment ecosystem, submit credentials below.
        </p>

        <form onSubmit={submit} data-testid="intake-form">
          <input
            type="text"
            name="company_website"
            tabIndex="-1"
            autoComplete="off"
            value={form.company_website}
            onChange={update("company_website")}
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            aria-hidden="true"
            data-testid="honeypot-field"
          />
          <div className="bk-form-grid">
            <div className="bk-form-group">
              <label htmlFor="applicant-name">Full Name / Company Representative</label>
              <input id="applicant-name" type="text" required placeholder="John Doe" value={form.name} onChange={update("name")} data-testid="input-name" />
            </div>
            <div className="bk-form-group">
              <label htmlFor="applicant-email">Contact Email Address</label>
              <input id="applicant-email" type="email" required placeholder="you@domain.com" value={form.email} onChange={update("email")} data-testid="input-email" />
            </div>
            <div className="bk-form-group">
              <label htmlFor="profile-type">Application Type</label>
              <select id="profile-type" required value={form.profile_type} onChange={update("profile_type")} data-testid="select-type">
                <option value="" disabled>Select Pipeline...</option>
                <option value="Carrier / Owner-Operator (Seeking Dispatch)">Carrier / Owner-Operator (Seeking Dispatch)</option>
                <option value="Logistics Coordinator / Dispatcher (Seeking Recruitment)">Logistics Coordinator / Dispatcher (Seeking Recruitment)</option>
              </select>
            </div>
            <div className="bk-form-group">
              <label htmlFor="mc-dot">MC / DOT Number (If Carrier)</label>
              <input id="mc-dot" type="text" placeholder="MC# or DOT# (Leave blank if recruiter)" value={form.mc_dot} onChange={update("mc_dot")} data-testid="input-mcdot" />
            </div>
            <div className="bk-form-group bk-form-full">
              <label htmlFor="background">Operational Background &amp; Fleet Capabilities</label>
              <textarea id="background" rows="4" placeholder="Detail your current equipment types (Flatbed, Reefer, Dry Van) or your professional logistics recruiting background here..." value={form.background} onChange={update("background")} data-testid="input-background" />
            </div>
            <div className="bk-form-group bk-form-full">
              <label htmlFor="document">Upload Document (MC Authority / COI / W-9 — optional)</label>
              <input id="document" type="file" className="bk-file-input" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="input-document" />
              <div className="bk-file-hint">PDF, JPG, PNG, DOC — max 10MB.</div>
            </div>
            <div className="bk-form-group bk-form-full">
              <button type="submit" className="bk-btn bk-btn-cyan bk-form-submit" disabled={loading} data-testid="submit-button">
                {loading ? (<><Loader2 size={16} className="bk-spin" /> Transmitting...</>) : (<>Transmit Pipeline Request <Send size={16} /></>)}
              </button>
            </div>
          </div>
          {status.type && (
            <div className={`bk-form-alert ${status.type}`} data-testid="form-status">{status.msg}</div>
          )}
        </form>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="bk-section" id="contact" data-testid="contact-section">
      <div className="bk-eyebrow">Reach Us</div>
      <h2 className="bk-section-title">Get In <span>Touch</span></h2>
      <div className="bk-contact">
        <div className="bk-contact-card" data-testid="contact-email">
          <div className="bk-cap-icon"><Mail size={24} /></div>
          <h4>Operations Pipeline</h4>
          <p><a href={`mailto:${OPS_EMAIL}`}>{OPS_EMAIL}</a></p>
        </div>
        <div className="bk-contact-card" data-testid="contact-hours">
          <div className="bk-cap-icon"><Clock size={24} /></div>
          <h4>Business Hours</h4>
          <p>Monday – Friday<br />5:00 AM – 4:00 PM</p>
        </div>
        <div className="bk-contact-card" data-testid="contact-area">
          <div className="bk-cap-icon"><Globe size={24} /></div>
          <h4>Service Model</h4>
          <p>Remote &middot; US-Based<br />Nationwide Dispatch</p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return <SiteFooter />;
}

function CookieConsent() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      if (!localStorage.getItem("bk_cookie_consent")) setVisible(true);
    } catch (e) {}
  }, []);

  const decide = (accepted) => {
    try {
      localStorage.setItem("bk_cookie_consent", accepted ? "accepted" : "declined");
    } catch (e) {}
    if (accepted && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: "granted",
        ad_user_data: "granted",
        ad_personalization: "granted",
        analytics_storage: "granted",
      });
    }
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div className="bk-cookie" data-testid="cookie-banner">
      <div className="bk-cookie-text">
        We use cookies and Google Analytics to understand site traffic and improve your experience. See our{" "}
        <Link to="/privacy" data-testid="cookie-privacy-link">Privacy Policy</Link>.
      </div>
      <div className="bk-cookie-actions">
        <button className="bk-btn bk-btn-cyan" onClick={() => decide(false)} data-testid="cookie-decline">Decline</button>
        <button className="bk-btn bk-btn-cyan-solid" onClick={() => decide(true)} data-testid="cookie-accept">Accept</button>
      </div>
    </div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return <motion.div className="bk-progress" style={{ scaleX }} data-testid="scroll-progress" />;
}

function EquipmentMarquee() {
  const items = [...EQUIPMENT, ...EQUIPMENT];
  return (
    <div className="bk-marquee" data-testid="equipment-marquee">
      <div className="bk-marquee-track">
        {items.map((e, i) => (
          <span className="bk-marquee-item" key={i}>
            <span className="bk-marquee-dot" /> {e}
          </span>
        ))}
      </div>
    </div>
  );
}

function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="bk-root" data-testid="app-root">
      <ScrollProgress />
      <div className="bk-mesh" />
      <div className="bk-grid-overlay" />
      <Constellation />
      <div className="bk-layer">
        <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <Hero />
        <EquipmentMarquee />
        <main className="bk-main">
          <Solutions />
          <HowItWorks />
          <Pricing />
          <DssModule />
          <Testimonials />
          <Policies />
          <FAQ />
          <Recruiting />
          <Contact />
        </main>
        <Footer />
      </div>
      <CookieConsent />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
